import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgComponentOutlet } from '@angular/common';
import { lookupPicker } from './picker-registry';
import { POPOVER_CONTEXT, PickerContext } from './picker.types';
import { DIM_TAB_APPLICABILITY } from '../state/dim-tab-applicability';
import { RatesFilterStateService } from '../../state/rates-filter-state.service';

/** Maps this host's `dimensionKey` to the clearDim() dim used by the
 *  Active-Filters footprint chips, so the picker can self-collapse when its
 *  own chip is cleared. (The two vocabularies differ — e.g. 'strat' clears
 *  via 'crediting', 'am-best' via 'amBest'.) */
const DIMKEY_TO_CLEARED_DIM: Record<string, string> = {
  'prem-type': 'premiumType',
  'am-best': 'amBest',
  'mktg': 'mktg',
  'index': 'index',
  'strat': 'crediting',
  'combo': 'combo',
  'surr': 'surrYears',
  'riders': 'riders',
  'state': 'states',
  'contribution': 'premium',
  'min-premium': 'minPremium',
  'buffer-level': 'bufferLevel',
  'floor-level': 'floorLevel',
  'segment-duration': 'segmentDuration',
  'rate-type': 'rateType',
  'protection-type': 'protectionType',
};

/**
 * Phase 1 (rail-forms-polish) — InlinePickerHostComponent.
 *
 * Renders a picker as a single clean "field-row": a clickable header
 * (field name + right-side summary pill + ★ pin) that expands/collapses
 * a flush control body (no border, no box, no × button, no repeated
 * title). Provides the same POPOVER_CONTEXT DI token the floating
 * popover used, so picker components are unchanged.
 *
 * Auto-expands when a selection exists (summary non-empty); the user
 * can override with toggle(). Emits (pinToggle) on star click. The
 * (closeRequest) output is retained for callers that still wire it,
 * though the × button is gone from the template.
 */
@Component({
  selector: 'app-inline-picker-host',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './inline-picker-host.component.html',
  styleUrl: './inline-picker-host.component.scss',
})
export class InlinePickerHostComponent implements OnChanges {
  @Input({ required: true }) dimensionKey!: string;
  @Input({ required: true }) optionsFn!: () => string[];
  @Input() pinned = false;
  @Input() pinDimKey: string | null = null;
  /**
   * Summary text (e.g. "Allianz +2", "$100k", "7, 10", "A+", or "All").
   *
   * 2026-06-04 icing #4: the host now treats the literal string "All"
   * (case-sensitive) as the "no-selection" marker and paints it muted+
   * italic. Any other non-empty value paints in the active style.
   *
   * For backward-compat, an empty string ('') is also treated as
   * "no-selection" — the previous contract.
   */
  @Input() summary = '';
  /** Muted text shown when summary is empty. */
  @Input() emptyLabel = 'All';
  /** Find-a-filter query — highlights the matched substring in the field name. */
  @Input() highlightQuery = '';
  /** Render only the control, always open — no field-row header. For a rail
   *  group whose only dimension this is: the group header already names it
   *  and carries its summary, so a second accordion row under it is just an
   *  extra click (same reasoning as the State Availability group). Pinning
   *  lives on the header row, so a headless field can't be pinned from here. */
  @Input() headless = false;

  @Output() pinToggle = new EventEmitter<string>();
  @Output() closeRequest = new EventEmitter<string>();

  // Plain methods (not computed): dimensionKey / pinDimKey are non-signal
  // @Inputs, so a computed() over them would never re-run — misleading.
  // Each @for instance has a fixed dimensionKey anyway; these lookups are
  // trivial and run per change-detection without issue.
  protected entry() {
    return lookupPicker(this.dimensionKey);
  }

  protected canPin(): boolean {
    const key = this.pinDimKey;
    return !!key && key in DIM_TAB_APPLICABILITY;
  }

  /** Collapse state. Auto-expand on selection unless the user has toggled. */
  private readonly _userToggled = signal(false);
  private readonly _expanded = signal(false);

  /** True when there's no active filter for this dim — host paints the
   *  summary muted+italic in this state. Treats '' (legacy contract) and
   *  the literal 'All' (icing #4) the same. */
  protected isEmpty(): boolean {
    return this.summary === '' || this.summary === 'All';
  }

  protected open(): boolean {
    if (this._userToggled()) return this._expanded();
    // Auto-expand only when there's an actual active selection — empty
    // ('' or 'All') counts as collapsed by default.
    return !this.isEmpty();
  }

  toggle(): void {
    // Capture the currently-visible state BEFORE marking user-toggled, so the
    // first click on an auto-expanded field collapses it (rather than no-op).
    const wasOpen = this.open();
    this._userToggled.set(true);
    this._expanded.set(!wasOpen);
  }

  /** Field name with the find-a-filter match wrapped in <mark class="ff-hit">. */
  protected nameHtml(label: string): string {
    const text = label;
    const q = this.highlightQuery.trim();
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (!q) return esc(text);
    const lc = text.toLowerCase();
    const lcq = q.toLowerCase();
    let out = '';
    let i = 0;
    while (i < text.length) {
      const hit = lc.indexOf(lcq, i);
      if (hit === -1) { out += esc(text.slice(i)); break; }
      out += esc(text.slice(i, hit));
      out += '<mark class="ff-hit">' + esc(text.slice(hit, hit + q.length)) + '</mark>';
      i = hit + q.length;
    }
    return out;
  }

  /** Cached injector — built ONCE per dimensionKey change. */
  protected hostInjector: Injector | null = null;

  private readonly parentInjector = inject(Injector);
  private readonly filterState = inject(RatesFilterStateService);

  constructor() {
    // Collapse this field when its Active-Filters chip is cleared (clearDim →
    // dimClearedRequest). Forces collapse regardless of prior expand state, so
    // a section left open by the user also closes on chip-clear. In-picker
    // unchecking does NOT bump dimClearedRequest, so it won't collapse here.
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      untracked(() => {
        if (req.tick === 0) return;
        if (DIMKEY_TO_CLEARED_DIM[this.dimensionKey] === req.dim) {
          this._userToggled.set(true);
          this._expanded.set(false);
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dimensionKey']) {
      this.rebuildInjector();
    } else if (this.hostInjector === null) {
      this.rebuildInjector();
    }
  }

  private rebuildInjector(): void {
    const entry = lookupPicker(this.dimensionKey);
    if (!entry) {
      this.hostInjector = null;
      return;
    }
    const ctx: PickerContext = {
      dimensionKey: this.dimensionKey,
      label: entry.label,
      field: entry.field,
      optionsFn: this.optionsFn,
      closeFn: () => this.closeRequest.emit(this.dimensionKey),
    };
    this.hostInjector = Injector.create({
      parent: this.parentInjector,
      providers: [{ provide: POPOVER_CONTEXT, useValue: ctx }],
    });
  }

  onStarClick(): void {
    if (!this.canPin()) return;
    this.pinToggle.emit(this.pinDimKey!);
  }

  onCloseClick(): void {
    this.closeRequest.emit(this.dimensionKey);
  }
}
